import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Menu, X, ArrowRight, Mail, Globe, Shield
} from "lucide-react";
import { fadeInUp } from "@/constants";

// SEO Component for page-specific meta tags
export const SEO = ({ title, description, keywords, path }) => {
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : "https://www.qbic-catalyst.com";
  const fullTitle = title ? `${title} | Qbic Catalyst Partners` : "Qbic Catalyst Partners | Consulting & Training Services";
  
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={`${baseUrl}${path || ''}`} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={`${baseUrl}${path || ''}`} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
    </Helmet>
  );
};

// Animated stat card with counter effect
export const StatCard = ({ end, suffix = "+", label, icon: Icon }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (hasAnimated) return;
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasAnimated(true);
          const duration = 1200;
          let startTime;
          const animate = (currentTime) => {
            if (!startTime) startTime = currentTime;
            const progress = Math.min((currentTime - startTime) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * end));
            if (progress < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [end, hasAnimated]);

  return (
    <motion.div
      ref={ref}
      variants={fadeInUp}
      className="p-6 md:p-8 bg-[#0A1628] rounded-lg border border-[#2A3648] card-hover flex flex-col h-full"
    >
      <Icon className="text-[#C9A962] mb-4" size={28} />
      <p className="stat-number mb-2">{count}{suffix}</p>
      <p className="text-[#A0ABC0] text-sm">{label}</p>
    </motion.div>
  );
};

// Navigation Component
export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []); // Empty deps — handleScroll is stable (no external closures)

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About Us' },
    { path: '/services', label: 'Services' },
    { path: '/programs', label: 'Programs' },
    { path: '/ai-catalyst', label: 'AI Catalyst\u2122' },
    { path: '/events', label: 'Events' },
    { path: '/contact', label: 'Contact' },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'glass py-4' : 'bg-transparent py-6'}`}>
      <div className="container-custom flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <img src="/qcp-logo.png" alt="Qbic Catalyst Partners" className="h-12 md:h-14" />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
              className={`nav-link text-sm font-medium tracking-wide ${location.pathname === link.path ? 'active' : ''}`}
            >
              {link.label}
            </Link>
          ))}
          <Link to="/contact" className="btn-primary text-sm" data-testid="nav-cta">
            Book Consultation
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="lg:hidden text-white p-2"
          onClick={() => setIsOpen(!isOpen)}
          data-testid="mobile-menu-btn"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden mobile-menu"
          >
            <div className="container-custom py-6 flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`text-lg font-medium py-2 ${location.pathname === link.path ? 'text-[#C9A962]' : 'text-white'}`}
                >
                  {link.label}
                </Link>
              ))}
              <Link to="/contact" className="btn-primary text-center mt-4" onClick={() => setIsOpen(false)}>
                Book Consultation
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

// Footer Component
export const Footer = ({ variant = "navy" }) => {
  const light = variant === "light";
  return (
    <footer className={light ? "theme-light tint-ivory border-t border-[#E8DFD1]" : "bg-[#080F1A] border-t border-[#2A3648]"} data-testid={`site-footer-${variant}`}>
      <div className="container-custom pt-16 pb-6">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <span className="text-[#C9A962] font-bold text-lg tracking-wide">Qbic Catalyst Partners</span>
            <p className="text-[#A0ABC0] text-sm mb-4">
              Turning Possibilities into Performance through Innovation and Expertise.
            </p>
            <div className="flex items-center gap-2 text-sm text-[#C9A962]">
              <Shield size={16} />
              <span>ISO/IEC 27001:2022 Certified</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm uppercase tracking-[0.2em] text-[#A0ABC0] mb-4">Quick Links</h4>
            <div className="flex flex-col gap-3">
              <Link to="/about" className="footer-link text-sm">About Us</Link>
              <Link to="/services" className="footer-link text-sm">Services</Link>
              <Link to="/programs" className="footer-link text-sm">Programs</Link>
              <Link to="/contact" className="footer-link text-sm">Contact</Link>
            </div>
          </div>

          {/* Programs */}
          <div>
            <h4 className="text-sm uppercase tracking-[0.2em] text-[#A0ABC0] mb-4">Programs</h4>
            <div className="flex flex-col gap-3">
              <Link to="/programs#sales-transformation" className="footer-link text-sm">Sales Transformation</Link>
              <Link to="/programs#ai-catalyst" className="footer-link text-sm">AI Catalyst&#8482;</Link>
              <Link to="/programs#intel-unnati" className="footer-link text-sm">Intel Unnati</Link>
              <Link to="/programs#future-workforce" className="footer-link text-sm">AI for Future Workforce</Link>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm uppercase tracking-[0.2em] text-[#A0ABC0] mb-4">Contact</h4>
            <div className="flex flex-col gap-3">
              <a href="mailto:info@qbiccatalyst.com" className="footer-link text-sm flex items-center gap-2">
                <Mail size={14} /> info@qbiccatalyst.com
              </a>
              <span className="footer-link text-sm flex items-center gap-2">
                <Globe size={14} /> www.qbic-catalyst.com
              </span>
            </div>
          </div>
        </div>

        <div className="section-divider mt-10 mb-6" />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="footer-registrations">
          {[
            { k: "CIN", v: "U70200KA2023PTC177394" },
            { k: "GST", v: "29AAACQ9266H1ZY" },
            { k: "MSME", v: "UDYAM-KR-03-0336154" },
            { k: "D&B D-U-N-S", v: "640171057" },
          ].map((r) => (
            <div key={r.k} className={`px-4 py-3 rounded-lg border ${light ? "border-[#E8DFD1] bg-white" : "border-[#2A3648] bg-[#0A1628]"}`} data-testid={`footer-reg-${r.k.split(" ")[0].toLowerCase().replace("&", "")}`}>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A962] mb-1">{r.k}</p>
              <p className={`font-mono text-xs md:text-sm break-words ${light ? "text-[#334155]" : "text-[#D0D6E0]"}`}>{r.v}</p>
            </div>
          ))}
        </div>

        <div className="section-divider mb-4" />

        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-[#A0ABC0] px-2">
          <p>&copy; {new Date().getFullYear()} Qbic Catalyst Partners. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link to="/terms" className="footer-link text-sm">Terms & Conditions</Link>
            <span className="text-[#5A6578]">|</span>
            <Link to="/privacy" className="footer-link text-sm">Privacy Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Scroll to top on route change
export const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};
